import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  createProjectflowTasks,
  listProjectflowColumns,
  listProjectflowProjects,
  submitProjectflowTranscript,
  suggestProjectflowTasks,
  type ProjectflowSuggestion,
} from '../lib/api';
import { Dialog, useAnimatedClose } from './Dialog';
import { Icon } from './Icon';

/** Ekezet-fuggetlen egyezes: az "altalanos" az "Általános feladatok"-at is megtalálja. */
const fold = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * Feladat a ProjectFlow-ba - egy uzenetbol vagy egy oleles atiratabol.
 *
 * ─── Ket forras, ket ut ─────────────────────────────────────────────────────
 *
 * Egy UZENETBOL egy feladat lesz, itt helyben: projekt, oszlop, es kesz. Egy
 * mondatot felbontatni penz es ido a semmiert.
 *
 * Az ATIRATOT viszont atadjuk a ProjectFlow-nak. A `submit_transcript` eszkoze
 * tobbet tud, mint amit mi epithetnenk: felelost is tippel a beszelonevekbol,
 * es a jovahagyo feluleten a projekt, az oszlop, a felelos es a szoveg is
 * igazithato - ott, ahol a feladatok amugy is elnek. Feladatot az sem hoz
 * letre magatol: visszaad egy cimet, ahol a kollega atnezi a javaslatokat.
 */
export function ProjectflowDialog({
  source,
  linked,
  onClose: closeNow,
  onLink,
}: {
  source: { messageId?: string; huddleId?: string };
  linked: boolean;
  onClose: () => void;
  onLink: () => void;
}) {
  const onClose = useAnimatedClose(closeNow);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title="Küldés a ProjectFlow-ba">
      {!linked ? (
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">
            A feladat a <span className="font-medium text-ink">te nevedben</span> jön létre a
            ProjectFlow-ban, ezért egyszer össze kell kötnöd a fiókodat. Utána a projektjeid is
            maguktól megjelennek itt.
          </p>
          <button
            type="button"
            onClick={onLink}
            className="rounded-control bg-accent px-3.5 py-2 text-sm font-semibold text-accent-ink"
          >
            ProjectFlow összekötése
          </button>
        </div>
      ) : source.huddleId ? (
        <TranscriptBody huddleId={source.huddleId} onClose={onClose} />
      ) : (
        <MessageBody messageId={source.messageId ?? ''} onClose={onClose} />
      )}
    </Dialog>
  );
}

/**
 * Az atirat atadasa - egy gomb, aztan egy cim.
 *
 * Szandekosan nincs itt projekt- es oszlopvalaszto: azt a masik oldal jovahagyo
 * felulete kerdezi meg, a felelossel es a szovegezessel egyutt. Ket helyen
 * kerdezni ugyanazt csak zavart okozna.
 */
function TranscriptBody({ huddleId, onClose }: { huddleId: string; onClose: () => void }) {
  const [sending, setSending] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  const send = async () => {
    setSending(true);
    setHiba(null);
    try {
      setUrl((await submitProjectflowTranscript(huddleId)).url);
    } catch (error) {
      setHiba(error instanceof Error ? error.message : 'Az átküldés nem sikerült.');
    } finally {
      setSending(false);
    }
  };

  if (url) {
    return (
      <div className="space-y-3">
        <p className="text-sm">
          Az átirat átment. A ProjectFlow elkészítette a feladat-javaslatokat &mdash; ott tudod
          átnézni és véglegesíteni őket.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-control bg-accent px-3.5 py-2 text-sm font-semibold text-accent-ink"
        >
          Javaslatok megnyitása
          <Icon name="megnyitas" size="sm" />
        </a>
        <p className="text-2xs text-ink-faint">
          Amíg ott nem hagyod jóvá, egyetlen feladat sem jön létre.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-muted">
        Az átiratot átadjuk a ProjectFlow-nak, ami feladat-javaslatokat készít belőle &mdash; a
        beszélők nevéből felelőst is tippel.{' '}
        <span className="font-medium text-ink">Feladat addig nem jön létre</span>, amíg ott át nem
        nézed őket.
      </p>

      {hiba && (
        <p role="alert" className="text-xs text-danger">
          {hiba}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-3 py-1.5 text-sm text-ink-muted"
        >
          Mégsem
        </button>
        <button
          type="button"
          disabled={sending}
          onClick={() => void send()}
          className="rounded-control bg-accent px-3.5 py-1.5 text-sm font-semibold text-accent-ink disabled:opacity-40"
        >
          {sending ? 'Átküldés…' : 'Átküldés a ProjectFlow-ba'}
        </button>
      </div>
    </div>
  );
}

function MessageBody({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const [projectId, setProjectId] = useState('');
  const [projectQuery, setProjectQuery] = useState('');
  const [columnName, setColumnName] = useState('');
  const [tasks, setTasks] = useState<(ProjectflowSuggestion & { keep: boolean })[]>([]);
  const [permalink, setPermalink] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<{ count: number; error: string | null } | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  const projects = useQuery({
    queryKey: ['projectflow', 'projects'],
    queryFn: listProjectflowProjects,
  });

  const columns = useQuery({
    queryKey: ['projectflow', 'columns', projectId],
    queryFn: () => listProjectflowColumns(projectId),
    enabled: projectId !== '',
  });

  /*
    A JAVASLATOK a megnyitaskor keszulnek el, nem a projekt kivalasztasakor.

    A modell-hivas par masodperc; ha a projekt utan indulna, a kollega epp
    akkor varna, amikor mar dontott. Igy a varakozas a valasztasok ideje alatt
    telik el.
  */
  const suggestions = useQuery({
    queryKey: ['projectflow', 'suggest', messageId],
    queryFn: () => suggestProjectflowTasks(messageId),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!suggestions.data) return;
    setTasks(suggestions.data.suggestions.map((item) => ({ ...item, keep: true })));
    setPermalink(suggestions.data.permalink);
  }, [suggestions.data]);

  // Az elso, nem "kesz" oszlop a kezenfekvo cel - de atallithato.
  useEffect(() => {
    if (!columns.data || columns.data.length === 0) return;
    setColumnName(
      (current) =>
        current || (columns.data.find((column) => !column.is_done) ?? columns.data[0]!).name,
    );
  }, [columns.data]);

  const kivalasztott = tasks.filter((task) => task.keep && task.title.trim().length > 0);

  const valasztott = (projects.data ?? []).find((project) => project.id === projectId) ?? null;

  /*
    A keresés a projekt nevere ES a csapat nevere is megy.

    A ProjectFlow-ban a projektek csapatokba vannak rendezve, es tobb csapatnak
    is van "Altalanos feladatok" nevu projektje - a csapatnev nelkul a talalatok
    megkulonboztethetetlenek lennenek.
  */
  const szo = fold(projectQuery.trim());
  const talalatok = (projects.data ?? [])
    .filter(
      (project) =>
        szo === '' ||
        fold(project.name).includes(szo) ||
        fold(project.team?.name ?? '').includes(szo),
    )
    .slice(0, 50);

  const send = async () => {
    if (!projectId || !columnName || kivalasztott.length === 0 || sending) return;
    setSending(true);
    setHiba(null);

    try {
      const result = await createProjectflowTasks({
        projectId,
        columnName,
        ...(permalink ? { permalink } : {}),
        tasks: kivalasztott.map(({ title, description }) => ({
          title: title.trim(),
          description,
        })),
      });
      setDone({ count: result.created.length, error: result.error });
    } catch (error) {
      setHiba(error instanceof Error ? error.message : 'A küldés nem sikerült.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-2">
        <p className="text-sm">
          {done.count === 0
            ? 'Egy feladat sem jött létre.'
            : `${done.count} feladat létrejött a ProjectFlow-ban.`}
        </p>
        {done.error && (
          <p role="alert" className="text-xs text-danger">
            A többi elakadt: {done.error}
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-3 py-1.5 text-sm"
        >
          Bezárás
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/*
            A PROJEKT keresessel, nem legordulovel.

            Egy ProjectFlow-adminnak otven projektje is lehet - abban egy sima
            legordulo gorgetese hosszabb, mint beirni harom betut. A csapatnev
            is latszik: tobb ceg hasznal "Altalanos feladatok" nevu projektet.
          */}
      {valasztott ? (
        <div className="flex flex-wrap items-center gap-2 rounded-control border border-border-soft bg-surface-sunken px-2.5 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {valasztott.name}
            {valasztott.team && (
              <span className="ml-1.5 font-normal text-ink-faint">· {valasztott.team.name}</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              setProjectId('');
              setColumnName('');
              setProjectQuery('');
            }}
            className="rounded-control px-2 py-0.5 text-xs text-ink-muted hover:text-ink"
          >
            Másik projekt
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 rounded-control border border-border bg-surface px-2.5 py-1.5 focus-within:border-accent">
            <span className="text-ink-faint">
              <Icon name="keres" size="sm" />
            </span>
            <input
              aria-label="Projekt keresése"
              autoFocus
              value={projectQuery}
              onChange={(event) => setProjectQuery(event.target.value)}
              placeholder="Melyik projektbe? Név vagy csapat…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
            />
          </div>

          {projects.isLoading ? (
            <p className="mt-2 text-xs text-ink-muted">Projektek betöltése…</p>
          ) : talalatok.length === 0 ? (
            <p className="mt-2 text-xs text-ink-faint">
              {projectQuery.trim()
                ? `Nincs találat erre: „${projectQuery.trim()}”.`
                : 'Nincs elérhető projekt.'}
            </p>
          ) : (
            <ul className="mt-1.5 max-h-56 overflow-y-auto rounded-control border border-border-soft">
              {talalatok.map((project) => (
                <li key={project.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setProjectId(project.id);
                      setColumnName('');
                    }}
                    className="flex w-full items-baseline gap-2 px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover"
                  >
                    <span className="min-w-0 truncate">{project.name}</span>
                    {project.team && (
                      <span className="ml-auto shrink-0 text-2xs text-ink-faint">
                        {project.team.name}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Az oszlop csak a projekt utan kerdes - egy ures, letiltott legordulo csak zaj. */}
      {valasztott && (
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          Hova kerüljön
          <select
            value={columnName}
            disabled={columns.isLoading}
            onChange={(event) => setColumnName(event.target.value)}
            className="rounded-control border border-border bg-surface-raised px-2 py-1.5 text-sm text-ink disabled:opacity-50"
          >
            {(columns.data ?? []).map((column) => (
              <option key={column.id} value={column.name}>
                {column.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {projects.isError && (
        <p role="alert" className="text-xs text-danger">
          A projektek nem érhetők el. Lehet, hogy újra össze kell kötnöd a fiókodat.
        </p>
      )}

      {suggestions.isLoading ? (
        <p className="py-3 text-sm text-ink-muted">Feladatok kigyűjtése…</p>
      ) : (
        <div className="space-y-2">
          {tasks.length > 1 && (
            <p className="text-xs text-ink-muted">
              {tasks.length} javaslat — vedd ki, ami nem kell, és írd át, ami nem pontos.
            </p>
          )}
          <ul className="space-y-2">
            {tasks.map((task, index) => (
              <li key={index} className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={task.keep}
                  onChange={(event) =>
                    setTasks((current) =>
                      current.map((item, i) =>
                        i === index ? { ...item, keep: event.target.checked } : item,
                      ),
                    )
                  }
                  className="mt-2 size-3.5 accent-[var(--accent)]"
                />
                <div className="min-w-0 flex-1">
                  <input
                    aria-label="Feladat címe"
                    value={task.title}
                    onChange={(event) =>
                      setTasks((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, title: event.target.value } : item,
                        ),
                      )
                    }
                    className={`w-full rounded-control border border-border bg-surface-raised px-2 py-1 text-sm ${
                      task.keep ? '' : 'opacity-50'
                    }`}
                  />
                  {task.description && (
                    <p className="mt-0.5 line-clamp-2 px-0.5 text-2xs text-ink-faint">
                      {task.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
            A FORRAS-LINK nem opcio, hanem a lenyeg fele.

            A feladatkezeloben allo feladat magaban nem mondja meg, honnan jott;
            egy honap mulva epp az a kerdes, mi hangzott el korulotte.
          */}
      {permalink && (
        <p className="flex items-center gap-1.5 text-2xs text-ink-faint">
          <Icon name="link" size="sm" />A leírás aljára bekerül a visszamutató hivatkozás.
        </p>
      )}

      {hiba && (
        <p role="alert" className="text-xs text-danger">
          {hiba}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-3 py-1.5 text-sm text-ink-muted"
        >
          Mégsem
        </button>
        <button
          type="button"
          disabled={!projectId || !columnName || kivalasztott.length === 0 || sending}
          onClick={() => void send()}
          className="rounded-control bg-accent px-3.5 py-1.5 text-sm font-semibold text-accent-ink disabled:opacity-40"
        >
          {sending
            ? 'Küldés…'
            : kivalasztott.length > 1
              ? `${kivalasztott.length} feladat küldése`
              : 'Küldés'}
        </button>
      </div>
    </div>
  );
}
